import { useCallback, useState } from 'react';
import './social-automation.css';

import AssetSelector from './AssetSelector';
import PreviewCanvas from './PreviewCanvas';
import QuoteGenerator from './QuoteGenerator';

const API_BASE = process.env.REACT_APP_API_BASE || 'https://panacea-n6yp.onrender.com/api';

function SocialAutomation() {
    const [currentStep, setCurrentStep] = useState(1);
    
    const [topic, setTopic] = useState('Mindset');
    const [customImage, setCustomImage] = useState(null);
    const [customAudio, setCustomAudio] = useState(null);

    const [quote, setQuote] = useState('');
    const [caption, setCaption] = useState('');
    const [alignment, setAlignment] = useState('center');
    const [platform, setPlatform] = useState('instagram');
    const [visualMode, setVisualMode] = useState('upload'); // 'upload' | 'generate'
    const [imagePrompt, setImagePrompt] = useState('');
    const [isGeneratingImage, setIsGeneratingImage] = useState(false);
    
    const [quoteLoading, setQuoteLoading] = useState(false);
    const [apiKey, setApiKey] = useState(localStorage.getItem('gemini_api_key') || '');
    const [showSettings, setShowSettings] = useState(false);

    const [isEditing, setIsEditing] = useState(false);

    /* ---- Navigation ---- */
    const nextStep = () => {
        if (currentStep === 1 && !topic.trim()) {
            alert('Please enter a topic first.');
            return;
        }
        if (currentStep === 2 && !quote.trim()) {
            alert('Please generate or enter a quote first.');
            return;
        }
        setCurrentStep(prev => Math.min(prev + 1, 3));
    };

    const prevStep = () => setCurrentStep(prev => Math.max(prev - 1, 1));

    /* ---- Generate Quote ---- */
    const handleGenerateQuote = useCallback(async () => {
        if (!topic.trim()) return;
        setQuoteLoading(true);
        setIsEditing(false);

        try {
            const res = await fetch(`${API_BASE}/quote`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ topic, userApiKey: apiKey, platform }),
            });
            const data = await res.json();

            if (data.success) {
                setQuote(data.quote);
                setCaption(data.caption || '');
                // Reset alignment if new generation
                setAlignment('center');
            } else {
                throw new Error(data.error || 'Quote generation failed');
            }
        } catch (err) {
            const fallback = {
                quote: platform === 'linkedin' ? 'Strategic Intent' : 'Real growth starts when you stop making excuses.',
                caption: 'Focus on your evolution. #growth #mindset'
            };
            setQuote(fallback.quote);
            setCaption(fallback.caption);
            setAlignment('center');
            console.warn('Backend not reachable, using fallback:', err.message);
        } finally {
            setQuoteLoading(false);
        }
    }, [topic, apiKey, platform]);

    /* ---- Generate AI Image ---- */
    const handleGenerateImage = async () => {
        if (!imagePrompt.trim()) {
            alert('Please enter an image description.');
            return;
        }
        setIsGeneratingImage(true);
        try {
            const res = await fetch(`${API_BASE}/assets/generate-image`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ prompt: imagePrompt, userApiKey: apiKey }),
            });
            const data = await res.json();
            if (data.success) {
                setCustomImage(data.imageUrl);
            } else {
                throw new Error(data.error || 'Image generation failed');
            }
        } catch (err) {
            console.error('Image gen error:', err);
            alert('Failed to generate image. Using a professional placeholder.');
            setCustomImage('https://images.unsplash.com/photo-1499750310107-5fef28a66643?q=80&w=1000&auto=format&fit=crop');
        } finally {
            setIsGeneratingImage(false);
        }
    };

    /* ---- Regenerate ---- */
    const handleRegenerate = useCallback(() => {
        handleGenerateQuote();
    }, [handleGenerateQuote]);

    /* ---- Edit toggle ---- */
    const handleEdit = useCallback(() => {
        setIsEditing((prev) => !prev);
    }, []);

    const handleSaveApiKey = (newKey) => {
        setApiKey(newKey);
        localStorage.setItem('gemini_api_key', newKey);
        setShowSettings(false);
    };

    const handleImageUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            const url = URL.createObjectURL(file);
            setCustomImage(url);
        }
    };

    const handleAudioUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            const url = URL.createObjectURL(file);
            setCustomAudio(url);
        }
    };

    return (
        <div className="sa-page">
            {/* Header */}
            <div className="sa-page__header">
                <div className="sa-page__header-top">
                    <div className="sa-page__badge">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                        </svg>
                        Social Studio
                    </div>
                    <button className="sa-settings-btn" onClick={() => setShowSettings(!showSettings)} title="API Settings">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/>
                        </svg>
                    </button>
                </div>
                <div className="sa-page__header-main">
                    <h1 className="sa-page__title">Social Media Automation</h1>
                    <div className="sa-platform-indicator">
                        {platform === 'instagram' ? (
                            <span className="sa-badge-ig">Instagram Mode</span>
                        ) : (
                            <span className="sa-badge-li">LinkedIn Mode</span>
                        )}
                    </div>
                </div>
                
                {/* Step Indicator */}
                <div className="sa-wizard-steps">
                    <div className={`sa-step ${currentStep >= 1 ? 'active' : ''} ${currentStep > 1 ? 'completed' : ''}`}>
                        <div className="sa-step__num">1</div>
                        <span className="sa-step__label">Uploads</span>
                    </div>
                    <div className="sa-step__line"></div>
                    <div className={`sa-step ${currentStep >= 2 ? 'active' : ''} ${currentStep > 2 ? 'completed' : ''}`}>
                        <div className="sa-step__num">2</div>
                        <span className="sa-step__label">Studio</span>
                    </div>
                    <div className="sa-step__line"></div>
                    <div className={`sa-step ${currentStep >= 3 ? 'active' : ''}`}>
                        <div className="sa-step__num">3</div>
                        <span className="sa-step__label">Export</span>
                    </div>
                </div>

                {/* Settings Panel */}
                {showSettings && (
                    <div className="sa-settings-panel">
                        <div className="sa-settings-panel__header">
                            <span className="sa-settings-panel__title">Gemini API Configuration</span>
                            <button className="sa-settings-panel__close" onClick={() => setShowSettings(false)}>&times;</button>
                        </div>
                        <div className="sa-settings-panel__body">
                            <p className="sa-settings-panel__desc">Your key is stored locally in your browser and never shared with anyone.</p>
                            <div className="sa-settings-panel__input-grp">
                                <input 
                                    type="password" 
                                    className="sa-settings-panel__input" 
                                    placeholder="Enter Google Gemini API Key"
                                    value={apiKey}
                                    onChange={(e) => setApiKey(e.target.value)}
                                />
                                <button className="sa-btn sa-btn--primary" onClick={() => handleSaveApiKey(apiKey)}>Save Key</button>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Wizard Content Container */}
            <div className="sa-wizard-container">
                <div className="sa-wizard-content">
                    {currentStep === 1 && (
                        <div className="sa-step-content animate-fade">
                            <AssetSelector
                                topic={topic}
                                platform={platform}
                                visualMode={visualMode}
                                imagePrompt={imagePrompt}
                                isGeneratingImage={isGeneratingImage}
                                customImage={customImage}
                                customAudio={customAudio}
                                onTopicChange={setTopic}
                                onPlatformChange={setPlatform}
                                onVisualModeChange={setVisualMode}
                                onImagePromptChange={setImagePrompt}
                                onGenerateImage={handleGenerateImage}
                                onImageUpload={handleImageUpload}
                                onAudioUpload={handleAudioUpload}
                            />
                        </div>
                    )}

                    {currentStep === 2 && (
                        <div className="sa-step-content animate-fade">
                            <QuoteGenerator
                                topic={topic}
                                platform={platform}
                                visualMode={visualMode}
                                loading={quoteLoading}
                                quote={quote}
                                caption={caption}
                                alignment={alignment}
                                isEditing={isEditing}
                                onGenerate={handleGenerateQuote}
                                onEdit={handleEdit}
                                onQuoteChange={setQuote}
                                onCaptionChange={setCaption}
                                onAlignmentChange={setAlignment}
                            />
                        </div>
                    )}

                    {currentStep === 3 && (
                        <div className="sa-step-content animate-fade">
                            <PreviewCanvas 
                                topic={topic} 
                                quote={quote} 
                                caption={caption}
                                alignment={alignment}
                                platform={platform}
                                visualMode={visualMode}
                                customImage={customImage}
                                customAudio={customAudio}
                            />
                        </div>
                    )}
                </div>

                {/* Navigation Buttons */}
                <div className="sa-wizard-nav">
                    <button 
                        className={`sa-btn sa-btn--outline ${currentStep === 1 ? 'hidden' : ''}`}
                        onClick={prevStep}
                    >
                        Back
                    </button>
                    <button 
                        className="sa-btn sa-btn--publish"
                        onClick={currentStep === 3 ? () => setCurrentStep(1) : nextStep}
                    >
                        {currentStep === 3 ? 'Start New' : 'Next Step'}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default SocialAutomation;
